// @file Landside Vuex Store.
// "Landside" is the term we use to describe the pages that the users interact with before takeoff
// e.g., home page, login page, and signup page
import type { VerificationCodePurpose } from '@@/types'
import { FetchError, fetchJson, fetchResponse, HTTPMethod } from '@padlet/fetch'
import getCsrfToken from 'bits/csrf_token'
import device from 'bits/device'
import { isEmail } from 'bits/email'
import { FACE_WITH_ROLLING_EYES_EMOJI, WAVING_HAND_EMOJI } from 'bits/emoji'
import { captureException, captureFetchException } from 'bits/error_tracker'
import { p__, __ } from 'bits/intl'
import { getSearchParams, navigateTo, transformCurrentUrl } from 'bits/location'
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH } from 'bits/numbers'
import { addQueryParams } from 'bits/url'
import { UserRole } from 'bits/user_model'
import { EmailAddressVerification as EmailAddressVerificationApi } from 'dashboard/padlet_api'
import { ApiErrorCode, HttpCode, SnackbarNotificationType } from 'enums'
import type { Plugin, StoreOptions } from 'vuex'
import browser from 'vuexstore/modules/browser'
import alertDialog from 'vuexstore/modules/global_alert_dialog'
import confirmationDialog from 'vuexstore/modules/global_confirmation_dialog'
import globalSnackbar from 'vuexstore/modules/global_snackbar'
import locale from 'vuexstore/modules/locale'
import logger, { shouldLog } from 'vuexstore/plugins/logger'

interface FormJsonResponse {
  redirect_to: string
}

interface JSONApiResponse<T> {
  data?: JSONApiData<T>
  errors?: JSONApiError[]
}

interface JSONApiData<T> {
  id: string
  type: string
  attributes: T
}

interface JSONApiError {
  status: string
  title: string
  detail: string
  code: string
  meta: {}
}

interface LoginManifestResponse {
  loginMethods: LoginMethod[]
  name: string
  logo: string
}

interface LoginResponse {
  loginUrl: string
}

interface SignupResponse {
  loginUrl?: string
  redirectUri?: string
  name: string
}

interface LoginMethod {
  name: string
  url?: string
  displayName?: string
  logo?: string
}

interface LandsideState {
  orgDomain: string
  orgDomainValidationMessage: string
  email: string
  username: string
  verificationCode: string
  verificationPurpose: VerificationCodePurpose
  password: string
  role: UserRole
  emailValidationMessage: string
  usernameValidationMessage: string
  verificationCodeValidationMessage: string
  passwordValidationMessage: string
  isEmailVerified: boolean

  // new states for the email OTP verification flow
  enforceEmailVerification: boolean
  isEmailValidatedForSignup: boolean
  isEmailVerifiedWithOtp: boolean

  isUsernameVerified: boolean
  isAwaitingServerResponse: boolean
  minimumPasswordLength: number
  maximumPasswordLength: number
  loginMethods: LoginMethod[]
  userLoginMethods: LoginMethod[]
  verifyMethods: LoginMethod[]
  tenantName: string
  tenantLogo: string
  codeChallenge: string | null
  codeChallengeMethod: string | null
  pkceState: string | null
  originAppClientId: string | null
  originAppRedirectUri: string | null
  dir: string
  inviteLinkCode: string
  isElectronApp: boolean
}

const normalizeThirdPartyLoginMethods = (methods: LoginMethod[]): Record<string, LoginMethod> => {
  const loginMethods: Record<string, LoginMethod> = {}

  for (const method of methods) {
    if (method.name === 'password' || method.name.startsWith('saml')) continue
    loginMethods[method.name] = method
  }

  return loginMethods
}

// TODO To refactor once we finalize the JSONAPI conversation
const buildSignUpRequestPayload = (
  email: string,
  password: string,
  role: string,
  inviteLinkCode: string,
  referralCode: string | null,
  codeChallenge: string | null,
  codeChallengeMethod: string | null,
  pkceState: string | null,
  originAppRedirectUri: string | null,
  originAppClientId: string | null,
): {} => {
  return {
    data: {
      type: 'User',
      attributes: {
        email,
        password,
        role,
        inviteLinkCode,
        referralCode,
        codeChallenge,
        codeChallengeMethod,
        state: pkceState,
        redirectUri: originAppRedirectUri,
        clientId: originAppClientId,
      },
    },
  }
}

const plugins: Array<Plugin<LandsideState>> = []

if (shouldLog) plugins.push(logger)

export const storeOptions: StoreOptions<LandsideState> = {
  plugins,
  modules: {
    browser,
    alertDialog,
    confirmationDialog,
    globalSnackbar,
    locale,
  },
  state: {
    orgDomain: '',
    orgDomainValidationMessage: '',
    email: '',
    username: '',
    verificationCode: '',
    verificationPurpose: 'signup',
    password: '',
    role: UserRole.DEFAULT,
    emailValidationMessage: '',
    verificationCodeValidationMessage: '',
    usernameValidationMessage: '',
    passwordValidationMessage: '',
    enforceEmailVerification: false,
    isEmailVerified: false,
    isEmailValidatedForSignup: false,
    isEmailVerifiedWithOtp: false,
    isUsernameVerified: false,
    isAwaitingServerResponse: false,
    minimumPasswordLength: MIN_PASSWORD_LENGTH,
    maximumPasswordLength: MAX_PASSWORD_LENGTH,
    loginMethods: [],
    userLoginMethods: [],
    verifyMethods: [],
    tenantName: '',
    tenantLogo: '',
    codeChallenge: null,
    codeChallengeMethod: null,
    pkceState: null,
    originAppClientId: null,
    originAppRedirectUri: null,
    dir: document.documentElement.getAttribute('dir') || 'auto',
    inviteLinkCode: '',
    isElectronApp: device.electronApp,
  },
  getters: {
    isEmailValid: (state): string => {
      return state.emailValidationMessage.length ? 'no' : 'yes'
    },
    isUsernameValid: (state): string => {
      return state.usernameValidationMessage.length ? 'no' : 'yes'
    },
    isVerificationCodeValid: (state): string => {
      return state.verificationCodeValidationMessage.length ? 'no' : 'yes'
    },
    isPasswordValid: (state): string => {
      return state.passwordValidationMessage.length ? 'no' : 'yes'
    },
    isPkceEnabled: (state): boolean => {
      return state.codeChallenge !== null && state.codeChallengeMethod !== null && state.pkceState !== null
    },
    thirdPartyAuthLoginMethods: (state): Record<string, LoginMethod> =>
      normalizeThirdPartyLoginMethods(state.loginMethods),
    userThirdPartyAuthLoginMethods: (state): Record<string, LoginMethod> =>
      normalizeThirdPartyLoginMethods(state.userLoginMethods),
    thirdPartyAuthVerifyMethods: (state): Record<string, LoginMethod> =>
      normalizeThirdPartyLoginMethods(state.verifyMethods),
    samlLoginMethods: (state): LoginMethod[] => state.loginMethods.filter((method) => method.name.startsWith('saml')),
    isPasswordLoginForTenantEnabled: (state): boolean => {
      const passwordLoginMethod = state.loginMethods.find((loginMethod) => loginMethod.name === 'password')
      return passwordLoginMethod !== undefined
    },
    isPkceLogin: (state): boolean => {
      return (
        state.codeChallenge !== null &&
        state.codeChallengeMethod !== null &&
        state.pkceState !== null &&
        state.originAppClientId !== null &&
        state.originAppRedirectUri !== null
      )
    },
  },
  mutations: {
    SET_ORG_DOMAIN(state, domain: string): void {
      state.orgDomain = domain
    },
    SET_ORG_DOMAIN_VALIDATION_MESSAGE(state, message: string): void {
      state.orgDomainValidationMessage = message
    },
    SET_EMAIL(state, email: string): void {
      state.email = email
    },
    SET_USERNAME(state, username: string): void {
      state.username = username
    },
    SET_VERIFICATION_CODE(state, code: string): void {
      state.verificationCode = code
    },
    SET_VERIFICATION_PURPOSE(state, purpose: VerificationCodePurpose): void {
      state.verificationPurpose = purpose
    },
    SET_PASSWORD(state, password: string): void {
      state.password = password
    },
    SET_USER_ROLE(state, role: UserRole): void {
      state.role = role
    },
    SET_MINIMUM_PASSWORD_LENGTH(state, value: number): void {
      state.minimumPasswordLength = value
    },
    SET_MAXIMUM_PASSWORD_LENGTH(state, value: number): void {
      state.maximumPasswordLength = value
    },
    SET_EMAIL_VALIDATION_MESSAGE(state, message: string): void {
      state.emailValidationMessage = message
    },
    SET_USERNAME_VALIDATION_MESSAGE(state, message: string): void {
      state.usernameValidationMessage = message
    },
    SET_VERIFICATION_CODE_VALIDATION_MESSAGE(state, message: string): void {
      state.verificationCodeValidationMessage = message
    },
    SET_PASSWORD_VALIDATION_MESSAGE(state, message: string): void {
      state.passwordValidationMessage = message
    },
    SET_IS_EMAIL_VERIFIED(state, value: boolean): void {
      state.isEmailVerified = value
    },
    SET_ENFORCE_EMAIL_VERIFICATION(state, value: boolean): void {
      state.enforceEmailVerification = value
    },
    SET_IS_EMAIL_VALIDATED_FOR_SIGNUP(state, value: boolean): void {
      state.isEmailValidatedForSignup = value
    },
    SET_IS_EMAIL_VERIFIED_WITH_OTP(state, value: boolean): void {
      state.isEmailVerifiedWithOtp = value
    },
    SET_IS_USERNAME_VERIFIED(state, value: boolean): void {
      state.isUsernameVerified = value
    },
    SET_IS_AWAITING_SERVER_RESPONSE(state, value: boolean): void {
      state.isAwaitingServerResponse = value
    },
    SET_LOGIN_METHODS(state, loginMethods: LoginMethod[]): void {
      state.loginMethods = loginMethods
    },
    SET_USER_LOGIN_METHODS(state, loginMethods: LoginMethod[]): void {
      state.userLoginMethods = loginMethods
    },
    SET_VERIFY_METHODS(state, verifyMethods: LoginMethod[]): void {
      state.verifyMethods = verifyMethods
    },
    SET_TENANT_NAME(state, name: string): void {
      state.tenantName = name
    },
    SET_TENANT_LOGO(state, logo: string): void {
      state.tenantLogo = logo
    },
    SET_CODE_CHALLENGE(state, codeChallenge: string): void {
      state.codeChallenge = codeChallenge
    },
    SET_CODE_CHALLENGE_METHOD(state, codeChallengeMethod: string): void {
      state.codeChallengeMethod = codeChallengeMethod
    },
    SET_PKCE_STATE(state, pkceState: string): void {
      state.pkceState = pkceState
    },
    SET_ORIGIN_APP_CLIENT_ID(state, originAppClientId: string): void {
      state.originAppClientId = originAppClientId
    },
    SET_ORIGIN_APP_REDIRECT_URI(state, originAppRedirectUri: string): void {
      state.originAppRedirectUri = originAppRedirectUri
    },
    SET_INVITE_LINK_CODE(state, code: string): void {
      state.inviteLinkCode = code
    },
  },
  actions: {
    async initializeState({ state, dispatch, commit }, startingState): Promise<void> {
      dispatch('browser/initializeState')

      if (startingState?.email) {
        dispatch('setEmail', startingState.email)
        dispatch('setUsername', startingState.email)

        await dispatch('fetchUserLoginManifest')

        if (state.usernameValidationMessage === '') {
          void dispatch('setIsUsernameVerified', true)
        }
        if (state.emailValidationMessage === '') {
          void dispatch('setIsEmailVerified', true)
        }
      }
      if (startingState?.loginMethods) {
        commit('SET_LOGIN_METHODS', startingState.loginMethods)
      }
      if (startingState?.tenantName) {
        commit('SET_TENANT_NAME', startingState.tenantName)
      }
      if (startingState?.tenantLogo) {
        commit('SET_TENANT_LOGO', startingState.tenantLogo)
      }
      if (startingState?.codeChallenge) {
        commit('SET_CODE_CHALLENGE', startingState.codeChallenge)
      }
      if (startingState?.codeChallengeMethod) {
        commit('SET_CODE_CHALLENGE_METHOD', startingState.codeChallengeMethod)
      }
      if (startingState?.pkceState) {
        commit('SET_PKCE_STATE', startingState.pkceState)
      }
      if (startingState?.originAppClientId) {
        commit('SET_ORIGIN_APP_CLIENT_ID', startingState.originAppClientId)
      }
      if (startingState?.originAppRedirectUri) {
        commit('SET_ORIGIN_APP_REDIRECT_URI', startingState.originAppRedirectUri)
      }

      if (startingState?.role) {
        dispatch('setRole', startingState.role)
      }

      if (startingState?.minimumPasswordLength) {
        dispatch('setMinimumPasswordLength', startingState.minimumPasswordLength)
      }

      if (startingState?.maximumPasswordLength) {
        dispatch('setMaximumPasswordLength', startingState.maximumPasswordLength)
      }

      if (startingState?.inviteLinkCode) {
        dispatch('setInviteLinkCode', startingState.inviteLinkCode)
      }

      if (startingState?.errorCode && startingState?.erroredSsoService) {
        void dispatch('openThirdPartyAuthErrorSnackbar', {
          errorCode: startingState?.errorCode,
          erroredSsoService: startingState.erroredSsoService,
        })
      } else if (startingState?.errorCode) {
        // handle more generic errors not specific to a third party auth service
        void dispatch('openErrorSnackbar', {
          errorCode: startingState?.errorCode,
        })
      }
    },

    async fetchUserLoginManifest({ state, commit, dispatch }): Promise<void> {
      try {
        await dispatch('setIsAwaitingServerResponse', true)

        const url = transformCurrentUrl(
          {},
          {
            path: '/api/auth/login',
            search: { email_or_username: state.username },
          },
        )
        const response: JSONApiResponse<LoginManifestResponse> = await fetchJson(url, {
          method: HTTPMethod.get,
        })
        commit('SET_USER_LOGIN_METHODS', response.data?.attributes?.loginMethods)
        commit('SET_IS_USERNAME_VERIFIED', true)
      } catch (e) {
        if (e instanceof FetchError) {
          if (e.status === HttpCode.NotFound) {
            try {
              const parsedErrorMessage = JSON.parse(e.message)
              const error = parsedErrorMessage.errors[0]

              if (error.code === ApiErrorCode.NO_USER) {
                commit('SET_USERNAME_VALIDATION_MESSAGE', __('User not found'))
              }
            } catch (jsonException) {
              captureException(jsonException)
            }
          } else {
            captureFetchException(e, { source: 'LandsideFetchUserLoginManifest' })
          }
        } else {
          captureException(e)
        }
      } finally {
        await dispatch('setIsAwaitingServerResponse', false)
      }
    },

    /* Organization subdomain verification page */
    setOrgDomain: ({ commit }, domain: string): void => {
      commit('SET_ORG_DOMAIN', domain.trim().toLowerCase())
    },
    setOrgDomainValidationMessage: ({ commit }, message: string): void => {
      commit('SET_ORG_DOMAIN_VALIDATION_MESSAGE', message)
    },
    async verifyOrgDomain({ state, getters, commit, dispatch }): Promise<void> {
      if (!state.orgDomain.length) {
        commit('SET_ORG_DOMAIN_VALIDATION_MESSAGE', __('Please fill out this field.'))
        return
      }

      dispatch('setIsAwaitingServerResponse', true)

      try {
        const url = transformCurrentUrl(
          {},
          {
            path: '/api/auth/org_domain_verify',
            search: { subdomain: state.orgDomain },
          },
        )
        const response: FormJsonResponse = await fetchJson(url, {
          method: HTTPMethod.get,
        })
        const loginUrl = response.redirect_to
        if (getters.isPkceLogin === true) {
          const pkceLoginUrl = addQueryParams(
            loginUrl,
            `code_challenge=${state.codeChallenge as string}&code_challenge_method=${
              state.codeChallengeMethod as string
            }&redirect_uri=${encodeURIComponent(state.originAppRedirectUri as string)}&client_id=${
              state.originAppClientId as string
            }&pkce_state=${state.pkceState as string}`,
          )
          navigateTo(pkceLoginUrl)
        } else {
          navigateTo(loginUrl)
        }
      } catch (e) {
        dispatch('setIsAwaitingServerResponse', false)

        if (e.status === HttpCode.UnprocessableEntity) {
          const parsedErrorMessage = JSON.parse(e.message)
          commit('SET_ORG_DOMAIN_VALIDATION_MESSAGE', parsedErrorMessage.error)
        } else if (e.status === HttpCode.RateLimitError) {
          commit('SET_ORG_DOMAIN_VALIDATION_MESSAGE', __('Our servers are overloaded. Please try again in a minute.'))
        } else if (e.status >= HttpCode.RateLimitError) {
          commit('SET_ORG_DOMAIN_VALIDATION_MESSAGE', __('Looks like our servers are down. Please try again in a bit.'))
        }
      }
    },

    /* Login and signup pages */
    setEmail: ({ commit }, email: string): void => {
      commit('SET_EMAIL', email.trim())
    },
    setUsername: ({ commit }, username: string): void => {
      commit('SET_USERNAME', username.trim())
    },
    setVerificationCode: ({ commit }, code: string): void => {
      commit('SET_VERIFICATION_CODE', code)
    },
    setVerificationPurpose: ({ commit }, purpose: string): void => {
      commit('SET_VERIFICATION_PURPOSE', purpose)
    },
    setPassword: ({ commit }, password: string): void => {
      commit('SET_PASSWORD', password)
    },
    setRole: ({ commit }, role: UserRole): void => {
      commit('SET_USER_ROLE', role)
    },
    setMinimumPasswordLength: ({ commit }, value: number): void => {
      commit('SET_MINIMUM_PASSWORD_LENGTH', value)
    },
    setMaximumPasswordLength: ({ commit }, value: number): void => {
      commit('SET_MAXIMUM_PASSWORD_LENGTH', value)
    },
    setEmailValidationMessage: ({ commit }, message: string): void => {
      commit('SET_EMAIL_VALIDATION_MESSAGE', message)
    },
    setUsernameValidationMessage: ({ commit }, message: string): void => {
      commit('SET_USERNAME_VALIDATION_MESSAGE', message)
    },
    setVerificationCodeValidationMessage: ({ commit }, message: string): void => {
      commit('SET_VERIFICATION_CODE_VALIDATION_MESSAGE', message)
    },
    setPasswordValidationMessage: ({ commit }, message: string): void => {
      commit('SET_PASSWORD_VALIDATION_MESSAGE', message)
    },
    setIsEmailVerified: ({ commit }, value: boolean): void => {
      commit('SET_IS_EMAIL_VERIFIED', value)
    },
    setEnforceEmailVerification: ({ commit }, value: boolean): void => {
      commit('SET_ENFORCE_EMAIL_VERIFICATION', value)
    },
    setIsEmailValidatedForSignup: ({ commit }, value: boolean): void => {
      commit('SET_IS_EMAIL_VALIDATED_FOR_SIGNUP', value)
    },
    setIsEmailVerifiedWithOtp: ({ commit }, value: boolean): void => {
      commit('SET_IS_EMAIL_VERIFIED_WITH_OTP', value)
    },
    setIsUsernameVerified: ({ commit }, value: boolean): void => {
      commit('SET_IS_USERNAME_VERIFIED', value)
    },
    setIsAwaitingServerResponse: ({ commit }, value: boolean): void => {
      commit('SET_IS_AWAITING_SERVER_RESPONSE', value)
    },
    setInviteLinkCode: ({ commit }, code: string): void => {
      commit('SET_INVITE_LINK_CODE', code)
    },

    /* Login page */
    async continueWithEmailOrUsername({ state, dispatch }): Promise<void> {
      if (state.username === '') return
      await dispatch('fetchUserLoginManifest')
    },
    async loginWithPassword({ state, getters, commit, dispatch }): Promise<void> {
      if (!state.username || !state.password) return

      try {
        dispatch('setIsAwaitingServerResponse', true)

        const url = `/api/auth/login`
        const response: JSONApiResponse<LoginResponse> = await fetchJson(url, {
          method: HTTPMethod.post,
          csrfToken: getCsrfToken(),
          jsonData: { username: state.username, password: state.password },
        })
        const loginUrl = response.data?.attributes?.loginUrl as string
        if (getters.isPkceLogin === true) {
          const pkceLoginUrl = addQueryParams(
            loginUrl,
            `code_challenge=${state.codeChallenge as string}&code_challenge_method=${
              state.codeChallengeMethod as string
            }&redirect_uri=${encodeURIComponent(state.originAppRedirectUri as string)}&client_id=${
              state.originAppClientId as string
            }&pkce_state=${state.pkceState as string}`,
          )
          navigateTo(pkceLoginUrl)
        } else {
          navigateTo(loginUrl)
        }
      } catch (e) {
        dispatch('setIsAwaitingServerResponse', false)

        if ([HttpCode.UnprocessableEntity, HttpCode.RateLimitError].includes(e.status)) {
          try {
            const parsedErrorMessage = JSON.parse(e.message)
            const error = parsedErrorMessage.errors[0]

            if (error.code === ApiErrorCode.NO_USER) {
              commit('SET_USERNAME_VALIDATION_MESSAGE', __('User not found'))
            } else if (error.code === ApiErrorCode.WRONG_PASSWORD) {
              commit('SET_PASSWORD_VALIDATION_MESSAGE', __('Wrong password'))
            } else if (error.code === ApiErrorCode.TOO_MANY_REQUESTS) {
              commit('SET_PASSWORD_VALIDATION_MESSAGE', __('Too many failed login attempts. Please try again later.'))
            }
          } catch (jsonException) {
            captureException(jsonException)
          }
        } else {
          captureFetchException(e, { source: 'LandsideLoginWithPassword' })
        }
      }
    },

    /* Signup page */
    promptLogin({ state, dispatch }): void {
      dispatch('confirmationDialog/openConfirmationDialog', {
        ...WAVING_HAND_EMOJI,
        title: __('Welcome back'),
        body: __('Looks like you already have an account with %{tenantName}. Would you like to log in instead?', {
          tenantName: state.tenantName,
        }),
        confirmButtonText: __('Continue to log in'),
        cancelButtonText: __('Nevermind'),
        afterConfirmActions: [{ type: 'redirectToLogin' }],
        forceFullWidthButtons: true,
      })
    },
    redirectToLogin({ state }): void {
      const url = transformCurrentUrl(
        {},
        {
          path: '/auth/login',
          search: { email: state.email },
        },
      )
      navigateTo(url)
    },
    openCreateAccountErrorDialog({ dispatch }): void {
      dispatch('alertDialog/openAlertDialog', {
        ...FACE_WITH_ROLLING_EYES_EMOJI,
        title: __('Unable to create account'),
        body: __('There was an issue with your request. Please try again later!'),
        closeButtonText: __('Okay'),
      })
    },
    async continueSignupWithEmail({ state, dispatch, commit }): Promise<void> {
      try {
        if (!state.email) return

        if (!isEmail(state.email)) {
          dispatch('setIsEmailVerified', false)
          commit('SET_EMAIL_VALIDATION_MESSAGE', __('Email is invalid'))
          return
        }

        dispatch('setIsAwaitingServerResponse', true)
        const url = transformCurrentUrl(
          {},
          {
            path: '/api/auth/signup/check_email',
            search: { email: state.email },
          },
        )
        const res = await fetchResponse(url, { method: HTTPMethod.get })
        try {
          const jsonResponse = await res.json()
          if (jsonResponse?.data?.attributes?.emailVerificationRequired === true) {
            dispatch('setEnforceEmailVerification', true)
          }
          if (jsonResponse?.data?.attributes?.verifyMethods != null) {
            commit('SET_VERIFY_METHODS', jsonResponse.data.attributes.verifyMethods)
          } else {
            // if verifyMethods is not returned, we should clear the verifyMethods state
            // since the user might go back and forth between screens and change to an email that does not have applicable SSO verify methods
            commit('SET_VERIFY_METHODS', [])
          }
        } catch (e) {
          // do nothing
        }
        dispatch('setIsEmailVerified', true)
        dispatch('setIsEmailValidatedForSignup', true)
      } catch (e) {
        dispatch('setIsEmailVerified', false)

        if (e.status === HttpCode.UnprocessableEntity) {
          try {
            const parsedErrorMessage = JSON.parse(e.message)
            const error = parsedErrorMessage.errors[0]

            if (error.code === ApiErrorCode.INVALID_EMAIL) {
              commit('SET_EMAIL_VALIDATION_MESSAGE', __('Email is invalid'))
              return
            } else if (error.code === ApiErrorCode.EMAIL_ALREADY_IN_USE) {
              commit('SET_EMAIL_VALIDATION_MESSAGE', __('Email is taken'))
              dispatch('promptLogin')
              return
            }
          } catch (jsonException) {
            captureException(jsonException)
          }
        } else {
          captureFetchException(e, { source: 'LandsideContinueSignupWithEmail' })
        }

        dispatch('openCreateAccountErrorDialog')
      } finally {
        dispatch('setIsAwaitingServerResponse', false)
      }
    },
    async continueWithOtpVerification({ state, dispatch, commit }): Promise<void> {
      if (!state.email || !state.verificationCode) return
      try {
        dispatch('setIsAwaitingServerResponse', true)
        await EmailAddressVerificationApi.verifyEmailAddress(
          state.email,
          state.verificationCode,
          state.verificationPurpose,
        )
        commit('SET_IS_EMAIL_VERIFIED_WITH_OTP', true)
      } catch (e) {
        if (e.status === HttpCode.UnprocessableEntity) {
          try {
            const parsedErrorMessage = JSON.parse(e.message)
            const errorCode = parsedErrorMessage?.errors?.[0]?.code
            if (errorCode === 'UNSUPPORTED_DOMAIN') {
              commit(
                'SET_VERIFICATION_CODE_VALIDATION_MESSAGE',
                __('Email address verification is not supported for your domain'),
              )
            } else if (errorCode === 'INCORRECT_VERIFICATION_CODE') {
              commit('SET_VERIFICATION_CODE_VALIDATION_MESSAGE', __('Incorrect verification code'))
            }
          } catch (jsonException) {
            captureException(jsonException)
          }
        } else if (e.status === HttpCode.RateLimitError) {
          void dispatch('globalSnackbar/setSnackbar', {
            message: __('Too many verification attempts. Please try again later.'),
            notificationType: SnackbarNotificationType.error,
          })
        } else {
          void dispatch('globalSnackbar/setSnackbar', {
            message: __('Unable to verify'),
            notificationType: SnackbarNotificationType.error,
          })
          captureFetchException(e, { source: 'LandsideContinueWithOtpVerification' })
        }
      } finally {
        dispatch('setIsAwaitingServerResponse', false)
      }
    },
    async completeSignupWithPassword({ getters, state, dispatch, commit }): Promise<void> {
      try {
        if (!state.email || !state.password) return

        if (state.password.length < state.minimumPasswordLength) {
          commit('SET_PASSWORD_VALIDATION_MESSAGE', __('Password is too short'))
          return
        }

        if (state.password.length > state.maximumPasswordLength) {
          commit('SET_PASSWORD_VALIDATION_MESSAGE', __('Password is too long'))
          return
        }

        dispatch('setIsAwaitingServerResponse', true)
        const signUpUrl = `/api/auth/signup/users`

        const referralCode = getSearchParams().referral_code
        const response: JSONApiResponse<SignupResponse> = await fetchJson(signUpUrl, {
          method: HTTPMethod.post,
          csrfToken: getCsrfToken(),
          jsonData: buildSignUpRequestPayload(
            state.email,
            state.password,
            state.role,
            state.inviteLinkCode,
            referralCode,
            state.codeChallenge,
            state.codeChallengeMethod,
            state.pkceState,
            state.originAppRedirectUri,
            state.originAppClientId,
          ),
        })
        if (getters.isPkceEnabled === false) {
          const loginUrl = response.data?.attributes?.loginUrl as string
          navigateTo(loginUrl)
        } else {
          const redirectUri = response.data?.attributes?.redirectUri as string
          navigateTo(redirectUri)
        }
      } catch (e) {
        if (e.status === HttpCode.UnprocessableEntity) {
          try {
            const parsedErrorMessage = JSON.parse(e.message)
            const error = parsedErrorMessage.errors[0]

            if (error.code === ApiErrorCode.INVALID_EMAIL) {
              commit('SET_PASSWORD_VALIDATION_MESSAGE', __('Email is invalid'))
            } else if (error.code === ApiErrorCode.EMAIL_ALREADY_IN_USE) {
              commit('SET_PASSWORD_VALIDATION_MESSAGE', __('Email is taken'))
              dispatch('promptLogin')
            }
          } catch (jsonException) {
            captureException(jsonException)
          }
        } else {
          captureFetchException(e, { source: 'LandsideCompleteSignupWithPassword' })
        }

        dispatch('openCreateAccountErrorDialog')
        dispatch('setIsAwaitingServerResponse', false)
      }
    },
    async resendVerificationCode({ state, dispatch, commit }): Promise<void> {
      if (!state.email) return
      try {
        dispatch('setIsAwaitingServerResponse', true)
        await EmailAddressVerificationApi.resendVerificationCode(state.email, state.verificationPurpose)
        void dispatch('globalSnackbar/setSnackbar', {
          message: __('Email sent'),
          notificationType: SnackbarNotificationType.success,
        })
      } catch (e) {
        if (e.status === HttpCode.RateLimitError) {
          void dispatch('globalSnackbar/setSnackbar', {
            message: __('Too many emails sent. Please try again later.'),
            notificationType: SnackbarNotificationType.error,
          })
        } else {
          captureFetchException(e, { source: 'LandsideResendVerificationCode' })
        }
      } finally {
        dispatch('setIsAwaitingServerResponse', false)
      }
    },
    openThirdPartyAuthErrorSnackbar(
      { dispatch },
      { errorCode, erroredSsoService }: { errorCode: string; erroredSsoService: string },
    ): void {
      if (errorCode === 'SSO_AUTO_CREATION_DISALLOWED') {
        void dispatch('globalSnackbar/setSnackbar', {
          message: p__(
            'Error message when user fails to login with Single Sign On (SSO), e.g. Google, Microsoft, and Apple because the tenant does not allow auto creation of accounts',
            'Sorry, we could not find anyone with your %{erroredSsoService} account email. Please contact your administrator to create an account for you.',
            { erroredSsoService },
          ),
          notificationType: SnackbarNotificationType.error,
        })
      } else if (errorCode === 'SSO_INVALID_INFO') {
        void dispatch('globalSnackbar/setSnackbar', {
          message: p__(
            'Error message when user fails to login with Single Sign On (SSO), e.g. Google, Microsoft, and Apple because the email is invalid',
            'Sorry, your %{erroredSsoService} account email is most likely invalid. Please contact your administrator to create an account for you.',
            { erroredSsoService },
          ),
          notificationType: SnackbarNotificationType.error,
        })
      } else if (errorCode === 'SSO_AUTO_LINKING_DISALLOWED') {
        void dispatch('globalSnackbar/setSnackbar', {
          message: p__(
            'Error message displayed when the user fails to log in with their Single Sign On (SSO) accounts - e.g. Login with Google, Microsoft, or Apple, because we do not allow auto linking of accounts, due to lack of verification or mismatch in account details',
            'Sorry, we could not log you in using %{erroredSsoService}. Please log in using your email and password instead.',
            { erroredSsoService },
          ),
          notificationType: SnackbarNotificationType.error,
        })
      } else {
        void dispatch('globalSnackbar/setSnackbar', {
          message: __('Something went wrong. Please try again later.'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    },
    openErrorSnackbar({ dispatch }, { errorCode }: { errorCode: string }): void {
      if (errorCode === 'UNABLE_TO_VERIFY') {
        void dispatch('globalSnackbar/setSnackbar', {
          // this error is triggered when user exceeds rate limit for verification attempts
          // but we display a generic error and use a non-descriptive error code in the url query param
          message: __('Something went wrong. Please try again later.'),
          notificationType: SnackbarNotificationType.error,
        })
      }
    },
  },
}

export type { LandsideState, LoginMethod }
